import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterHeader from '../FilterHead';
import SearchSection from './SearchSection';
import RowSection from './RowSection';
import { getAppliedFilterValue } from '../../../../../helpers/utils';
import OmnitureUtils from '../../../../../../common/utils/Omniture/OmnitureUtils';

import './styles.scss';

const BATCH_SIZE = 4;
const totalBatches = 3;

const ListFilter = ({
  isSearchSection,
  filterData,
  singleSelect,
  handleFilterChange,
  showOverlayFilterLoader,
  filterIndex,
  isCollapsedOnInit = false,
}) => {
  const [isCollapsed, setCollapsed] = useState(!isCollapsedOnInit);
  const [shownFiltersSize, setShownFiltersSize] = useState(BATCH_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [search] = useSearchParams();

  const items = useMemo(() => {
    if (filterData?.urlParam === 'premiumPackage') {
      return filterData.listingFilterValues?.slice(0, 1);
    }
    if (!searchValue) return filterData.listingFilterValues;

    return filterData.listingFilterValues.filter(
      filterOption =>
        `${filterOption?.filterText}`.toLowerCase().indexOf(`${searchValue}`.toLowerCase()) > -1
    );
  }, [searchValue]);

  const hasMoreOptions = useMemo(() => {
    const optionsLength = items?.length;

    return optionsLength > shownFiltersSize;
  }, [filterData, shownFiltersSize, items]);

  const appliedValues = useMemo(
    () => getAppliedFilterValue(filterData?.urlParam, search),
    [filterData?.urlParam, search]
  );

  const increaseBatchSize = useCallback(() => {
    OmnitureUtils.trackClickEvent({ prop54: `filter_apply_ui_${filterData?.urlParam}_Show_More` });
    setShownFiltersSize(currentValue => {
      if (currentValue >= totalBatches * BATCH_SIZE) {
        return items.length;
      }

      return currentValue + BATCH_SIZE;
    });
  }, [filterData, items]);

  const resetBatchSize = useCallback(() => {
    OmnitureUtils.trackClickEvent({ prop54: `filter_apply_ui_${filterData?.urlParam}_Show_Less` });
    setShownFiltersSize(BATCH_SIZE);
  }, [filterData]);

  const handleCollapsed = () => {
    setCollapsed(!isCollapsed);
  };

  const handleOptionClick = useCallback(
    (values, { prop54 } = {}) => {
      OmnitureUtils.trackClickEvent({
        prop54: prop54.replace('<urlParam>', filterData?.urlParam),
      });
      handleFilterChange({ urlParam: filterData?.urlParam, values });
    },
    [handleFilterChange]
  );

  return (
    <div className="filtersOuter">
      {showOverlayFilterLoader && <div className="filtersOverlayLoader" />}
      <FilterHeader
        isCollapsed={isCollapsed}
        handleCollapsed={handleCollapsed}
        name={filterData?.name}
        filterIndex={filterIndex}
      />

      {isCollapsed && (
        <div className="innerListSection">
          {isSearchSection && <SearchSection value={searchValue} onChange={setSearchValue} />}
          {items?.slice(0, shownFiltersSize)?.map(filterOption => (
            <RowSection
              key={filterOption?.uniqueId}
              filterOption={filterOption}
              filterStats={filterData?.filterStats?.discreteStats}
              handleOptionClick={handleOptionClick}
              appliedValues={appliedValues}
              singleSelect={singleSelect}
              filterUrlParam={filterData?.urlParam}
            />
          ))}
          {hasMoreOptions && (
            <p>
              <span onClick={increaseBatchSize} className="linkText boldFont">
                Show More
              </span>
            </p>
          )}
          {shownFiltersSize >= items?.length && items?.length > BATCH_SIZE && (
            <p>
              <span onClick={resetBatchSize} className="linkText boldFont">
                Show Less
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ListFilter;
